import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

const bgImage = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/4nmehjm4_FRAUD%20IMAGE.png';

const glossaryTerms = [
  {
    term: 'Acquirer or Acquiring Bank',
    definition: 'An acquirer or acquiring bank is a financial institution or bank that processes credit or debit card payments on behalf of a merchant. Acquirers/Acquiring Banks are registered members of card schemes, such as Mastercard or Visa, and acquire transactions on these networks on behalf of the merchant. This network connects acquirers to issuers allowing customer transactions to be verified and subsequently approved/declined. An Acquirer also assumes full risk and responsibility associated with those transactions.',
  },
  {
    term: 'Address Verification System (AVS)',
    definition: "The Address Verification System is designed to prevent fraudulent activity by allowing a merchant to verify whether a credit card's given address matches the address in the card issuer's system. This system is especially useful in situations where a buyer and a seller do not complete their transaction in person.",
  },
  {
    term: 'Authorisation Code',
    definition: 'An authorisation code is sent from an Issuing Bank with the message to either authorise or decline a sale. This process is designed to check if the cardholder has sufficient funds.',
  },
  {
    term: 'Bank Identification Number (BIN)',
    definition: 'The Bank Identification Number identifies who the card issuing bank is and their geographic location.',
  },
  {
    term: 'Cardholder',
    definition: 'A cardholder is an individual, merchant or a corporate individual to whom a credit card is issued. The cardholder is the person or company who opens a credit/debit card account and makes purchases using that respective credit/debit card.',
  },
  {
    term: 'Issuing Bank',
    definition: "Issuing Bank is responsible for any cardholder's ability to make a payment with the credit/debit card given by the bank.",
  },
  {
    term: 'Merchant',
    definition: "A merchant works with an acquirer and applies for a merchant account (an account that allows the merchant to accept credit and debit cards). Whenever a customer purchases an item with a credit or debit card, the merchant submits the purchase transaction information to its acquiring bank, who submit it through the card association scheme to the cardholder's issuing bank. The issuing bank will approve or decline the charge and bill the cardholder the amount due to the merchant.",
  },
  {
    term: 'Merchant Account',
    definition: 'A merchant account is an account number issued by an acquirer to a specific Merchant.',
  },
  {
    term: 'Merchant Category Code (MCC)',
    definition: "A merchant category code is first assigned to a merchant once it begins to accept VISA or Mastercard credit/debit cards. If a merchant is involved in more than one type of business, then the Merchant Category Code will apply to that merchant's primary line of business.",
  },
  {
    term: 'Payment Gateway',
    definition: 'A Payment Gateway transmits transaction information to an acquirer and responses from issuing banks (such as whether a transaction is approved or declined). Essentially, payment gateways facilitate communication within banks.',
  },
  {
    term: 'Payment Processor',
    definition: 'A Payment Processor enables merchants to receive debit or Credit Card payments online by providing a connection to an acquiring bank. The processors perform many functions such as evaluating whether transactions are valid and approved, using anti-fraud measures to ensure that a purchase transaction is initiated by the source it claims to be. Processors are governed by standards and regulations organised by the card schemes.',
  },
  {
    term: 'Payment Service Provider (PSP)',
    definition: 'Payment Service Providers partner with acquiring banks to offer merchants the capability to accept payments. Payment Service Providers often offer services in addition to processing transactions. These services include Payment Card Industry Data Security Standard (PCI DSS) compliance, fraud protection, and the ability to process different currencies.',
  },
  {
    term: 'PCI DSS (Payment Card Industry Data Security Standard)',
    definition: 'The Payment Card Industry Data Security Standard is maintained by the Payment Card Industry Security Standards Council. The purpose of PCI DSS compliance is to build and maintain a secure network, to protect cardholder data, to maintain a vulnerability management program, whilst implementing strong access control measures and maintaining an information security policy.',
  },
  {
    term: 'Recurring Transaction',
    definition: "A recurring transaction facilitates a customer's request to be billed on a periodic basis (usually once a month) in exchange for a product/service on an ongoing basis. The amount which the customer is billed may vary, depending on the service.",
  },
  {
    term: 'Transaction Settlement',
    definition: "The term 'settlement' is also used to refer to the specific amount of funds transferred by an acquirer to a merchant for the acceptance of a card transaction.",
  },
];

const Glossary = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col" data-testid="glossary-page">
      <Header />

      <div className="h-20" />

      {/* Full-page background image */}
      <div className="relative flex-1">
        <div
          className="fixed inset-0 top-20 bg-cover bg-center"
          style={{
            backgroundImage: `url(${bgImage})`,
            filter: 'blur(1px)',
          }}
        />
        <div className="fixed inset-0 top-20 bg-white/20" />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          <button
            onClick={() => navigate('/help-centre')}
            className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-8 transition-colors duration-200"
            data-testid="glossary-back-button"
          >
            <ArrowLeft size={18} />
            Back to Help Centre
          </button>

          {/* Intro */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3" data-testid="glossary-title">
              Glossary
            </h1>
            <p className="text-lg text-gray-600 font-bold">
              Common Terms & Acronyms in the Payments Industry
            </p>
          </div>

          {/* Glossary Terms */}
          <div className="space-y-4 mb-10">
            {glossaryTerms.map((item, index) => (
              <div
                key={index}
                className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-6 sm:p-8"
                data-testid={`glossary-term-${index}`}
              >
                <h3 className="text-lg font-bold text-[#1F4E79] mb-2">
                  {item.term}
                </h3>
                <p className="text-gray-700 text-base leading-relaxed">
                  {item.definition}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
      <BackToTop />
    </div>
  );
};

export default Glossary;
